/**
 * Client-side validation for the writable `PATCH /admin/settings` keys.
 *
 * This MIRRORS the server's rules (docs/products/mcp.md), it does
 * not replace them — the 422 remains the backstop and its message is what gets
 * shown. Validating here just means the operator finds out before submitting.
 *
 * The rate-limit keys are DELIBERATELY ABSENT. Rate limits are per tier, in
 * each tier's quota document (`PATCH /admin/quota/{tier}`) — sending any
 * `ratelimit/*` key here 422s. `components/mcp/TierRateLimits.tsx` has its own
 * validation (a live normalized-rate check, not a static per-value bound)
 * because the real hazard is the PAIR of numbers, not either one alone.
 *
 * One rule left is worth catching early for a real reason: a message
 * template missing a required {placeholder} raises TypeError at .format()
 * time, turning a merchant-facing quota message into a live 500.
 */

export type SettingKind = "boolean" | "number" | "template";

export interface SettingRule {
  kind: SettingKind;
  min?: number;
  max?: number;
  /** Placeholders the server .format()s. Missing one is a runtime 500. */
  required?: string[];
  /** Why the bound exists — shown inline, not in a tooltip. */
  hazard?: string;
}

export const SETTING_RULES: Record<string, SettingRule> = {
  "audit/gate_rows_enabled": { kind: "boolean" },

  "response/max_chars": {
    kind: "number",
    min: 1000,
    max: 2000000,
    hazard: "Too low truncates real answers; too high risks oversized responses.",
  },

  "usage/upsell_message": {
    kind: "template",
    required: ["tool", "tier", "reset_in"],
  },
  "usage/blocked_message": { kind: "template", required: ["tool", "tier"] },
  // The daily-total refusal names no tool: it is every tool. MCP has always
  // accepted it; with no rule here the console called it "not writable" and
  // never sent the edit.
  "usage/total_message": { kind: "template", required: ["tier", "cap", "reset_in"] },
  // A paused tool's refusal (MCP #337). Live within ~5 minutes, no Apply.
  "availability/paused_message": { kind: "template", required: ["tool"] },
  "blocklist/blocked_message": { kind: "template", required: [] },
  // Prepended to EVERY tool response as a prompt-injection defence — plain
  // text, no placeholders, same posture as blocklist/blocked_message. It was
  // missing from this table entirely (not a deliberate omission, unlike the
  // ratelimit/* keys above), which made a genuinely writable server key
  // render "This key is not writable" client-side only — the 422 backstop
  // was never reached because nothing was ever sent.
  "output_guard/untrusted_framing": { kind: "template", required: [] },
};

/**
 * The longest value MCP stores, in characters. Above it MCP answers a 500
 * (DA-3816). Characters, not bytes: 2,049 Arabic letters (4,098 bytes) save
 * fine. Counted as code points, so an emoji is one character, not two.
 */
export const SETTING_MAX_CHARS = 4096;

const PLACEHOLDER = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;

/** Every `{name}` in a template, in order of appearance. */
export function placeholdersIn(text: string): string[] {
  return [...text.matchAll(PLACEHOLDER)].map((m) => m[1]!);
}

/**
 * The reason this value would be refused, or null.
 *
 * `writable` is whether MCP's own `GET /admin/settings` lists the key in
 * `writable[]`. That list is the authority on what may be written: a key in it
 * with no rule here is plain text, refused only when empty or too long
 * (DA-3821). Without it, a key with no rule is still read-only — the console
 * does not guess that an unknown key is writable.
 */
export function validateSetting(
  key: string,
  raw: string,
  { writable = false }: { writable?: boolean } = {},
): string | null {
  const rule = SETTING_RULES[key];
  if (!rule && !writable) return "This key is not writable.";

  const value = raw.trim();
  if (value === "") return "Cannot be empty.";

  // The raw value, as sent: MCP stores it unstripped.
  const length = [...raw].length;
  if (length > SETTING_MAX_CHARS) {
    return `At most ${SETTING_MAX_CHARS.toLocaleString("en-US")} characters (${length.toLocaleString("en-US")} now).`;
  }

  if (!rule) return null;

  if (rule.kind === "boolean") {
    return value === "true" || value === "false"
      ? null
      : 'Must be exactly "true" or "false" — anything else is refused rather than coerced.';
  }

  if (rule.kind === "number") {
    if (!/^-?\d+$/.test(value)) return "Must be a whole number.";
    const n = Number(value);
    if (rule.min !== undefined && n < rule.min) return `Minimum is ${rule.min}.`;
    if (rule.max !== undefined && n > rule.max) return `Maximum is ${rule.max}.`;
    return null;
  }

  // SSM refuses a value with `{{` (its parameter-reference syntax), so no stored
  // message can show a literal brace; MCP refuses `{{` and `}}` with a 422.
  if (value.includes("{{") || value.includes("}}")) {
    return "Cannot contain {{ or }} — SSM cannot store them. Use braces only for the placeholders, written once: {name}.";
  }

  // Template. Both directions matter: a MISSING placeholder raises TypeError
  // at .format() time, and an UNLISTED one raises KeyError. Either turns a
  // refusal message into a 500 the first time a merchant hits it.
  const required = rule.required ?? [];

  // A template MCP `.format()`s cannot hold a brace that is not part of a
  // `{name}` placeholder: a trailing `{`, a lone `}`, `{}`, `{ }`, `{0}`. MCP
  // refuses it, and before it did, it was a ValueError on the merchant's next
  // refusal (DA-3815). Only where MCP formats: the blocked message and the
  // framing are never formatted, and MCP stores a brace in them as text.
  if (required.length > 0 && /[{}]/.test(value.replace(PLACEHOLDER, ""))) {
    return "Stray { or } — braces are only for placeholders, written as {name}.";
  }

  const found = new Set(placeholdersIn(value));
  const missing = required.filter((p) => !found.has(p));
  const unlisted = [...found].filter((p) => !required.includes(p));

  if (missing.length) {
    return `Missing ${missing.map((p) => `{${p}}`).join(", ")} — the server substitutes ${
      missing.length === 1 ? "it" : "them"
    } at runtime, so leaving ${missing.length === 1 ? "it" : "them"} out fails on first use.`;
  }
  if (unlisted.length) {
    return `${unlisted.map((p) => `{${p}}`).join(", ")} ${
      unlisted.length === 1 ? "is" : "are"
    } not substituted — the message would fail on first use.`;
  }
  return null;
}
